import React, { useMemo } from 'react';
import { prepareSchematicSlots } from '../../../helpers/siteMapSlotHelpers';

const LINE_HEIGHT = 1.2;

const RotatedLabelText = ({
  slotId,
  lines,
  fontSize,
  centerX,
  centerY,
  rotationDeg
}) => {
  if (!lines?.length || !fontSize) return null;

  const startY = centerY - ((lines.length - 1) * fontSize * LINE_HEIGHT) / 2;

  return (
    <g transform={`rotate(${rotationDeg}, ${centerX}, ${centerY})`}>
      <text
        x={centerX}
        y={startY}
        fill="#000000"
        fontSize={fontSize}
        fontWeight="600"
        fontFamily="Arial, Helvetica, sans-serif"
        textAnchor="middle"
        dominantBaseline="hanging"
        pointerEvents="none"
      >
        {lines.map((line, lineIndex) => (
          <tspan
            key={`${slotId}-body-${lineIndex}`}
            x={centerX}
            dy={lineIndex === 0 ? 0 : fontSize * LINE_HEIGHT}
          >
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

const SiteMapSchematic = ({ slots, className = '', mapSize = null }) => {
  const schematic = useMemo(
    () =>
      prepareSchematicSlots(slots, {
        viewWidth: mapSize?.width,
        viewHeight: mapSize?.height
      }),
    [slots, mapSize]
  );

  if (!schematic.slots.length) {
    return <div className="text-muted text-center py-4">No slot shapes to display.</div>;
  }

  return (
    <svg
      className={className}
      viewBox={`0 0 ${schematic.viewWidth} ${schematic.viewHeight}`}
      width={mapSize?.width ?? '100%'}
      height={mapSize?.height ?? 'auto'}
      preserveAspectRatio={mapSize ? 'none' : 'xMidYMid meet'}
      role="img"
      aria-label="Site map schematic"
    >
      <rect x="0" y="0" width={schematic.viewWidth} height={schematic.viewHeight} fill="#ffffff" />
      {schematic.slots.map(
        ({
          slot,
          pathD,
          colors,
          labelFrame,
          slotNumber,
          bodyLines,
          bodyFontSize,
          numberFontSize,
          cornerPosition
        }) => (
          <g key={slot._id}>
            <path
              d={pathD}
              fill={colors.fill}
              stroke={colors.stroke}
              strokeWidth={1.5}
              vectorEffect="non-scaling-stroke"
            />
            {slotNumber && numberFontSize && labelFrame.lineWidth > 0 && labelFrame.stackHeight > 0 && (
              <g
                transform={`rotate(${labelFrame.rotationDeg}, ${cornerPosition.x}, ${cornerPosition.y})`}
              >
                <text
                  x={cornerPosition.x}
                  y={cornerPosition.y}
                  fill="#000000"
                  fontSize={numberFontSize}
                  fontWeight="700"
                  fontFamily="Arial, Helvetica, sans-serif"
                  textAnchor="start"
                  dominantBaseline="hanging"
                  pointerEvents="none"
                >
                  {slotNumber}
                </text>
              </g>
            )}
            {bodyLines.length > 0 && bodyFontSize > 0 && labelFrame.lineWidth > 0 && (
              <RotatedLabelText
                slotId={slot._id}
                lines={bodyLines}
                fontSize={bodyFontSize}
                centerX={labelFrame.cx}
                centerY={labelFrame.cy}
                rotationDeg={labelFrame.rotationDeg}
              />
            )}
          </g>
        )
      )}
    </svg>
  );
};

export default SiteMapSchematic;
